import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, Search, FolderOpen, Loader2, Star } from "lucide-react";

export default function Library() {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/my/documents"), api.get("/categories")])
      .then(([d, c]) => { setDocs(d.data); setCategories(c.data); })
      .finally(() => setLoading(false));
  }, []);

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  const toggleBookmark = async (doc) => {
    setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, bookmarked: !d.bookmarked } : d)));
    try {
      await api.post(`/portal/bookmark/${doc.id}`);
    } catch {
      toast.error("Could not update bookmark.");
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, bookmarked: doc.bookmarked } : d)));
    }
  };

  const filtered = docs.filter((d) => {
    const matchCat = active === "all" || d.category === active;
    const matchQ = !query || d.title.toLowerCase().includes(query.toLowerCase());
    return matchCat && matchQ;
  });

  const usedCats = [...new Set(docs.map((d) => d.category))];

  const download = async (doc) => {
    setDownloading(doc.id);
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_name || doc.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div>
      <h1 className="font-heading font-black text-3xl tracking-tight">Document Library</h1>
      <p className="text-muted-foreground mt-2">{docs.length} documents available to you.</p>

      {docs.length === 0 ? (
        <div className="bg-white border border-border p-10 text-center mt-8">
          <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-bold text-lg">Nothing here yet</h3>
          <p className="text-muted-foreground mt-2">Purchase a product to unlock documents.</p>
          <Link to="/products" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold mt-6 btn-lift">
            Browse products
          </Link>
        </div>
      ) : (
        <>
          {docs.every((d) => !d.bookmarked) && !localStorage.getItem("cbs_bookmark_nudge_dismissed") && (
            <div className="mt-8 flex items-start gap-3 border border-amber-200 bg-amber-50 px-4 py-3" data-testid="bookmark-nudge">
              <Star className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="currentColor" />
              <p className="text-sm text-amber-900 flex-1">
                <span className="font-semibold">Star your go-to forms.</span> Tap the star on any document to pin it to <span className="font-semibold">Quick Access</span> on your dashboard — so your busiest forms are one tap away.
              </p>
              <button
                onClick={() => { localStorage.setItem("cbs_bookmark_nudge_dismissed", "1"); setDocs((p) => [...p]); }}
                data-testid="bookmark-nudge-dismiss"
                className="text-amber-700 hover:text-amber-900 text-xs font-semibold shrink-0">
                Got it
              </button>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search documents…" data-testid="library-search"
                className="w-full border border-border pl-10 pr-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            <Chip active={active === "all"} onClick={() => setActive("all")}>All ({docs.length})</Chip>
            {usedCats.map((slug) => (
              <Chip key={slug} active={active === slug} onClick={() => setActive(slug)}>
                {catName(slug)}
              </Chip>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            {filtered.map((d) => (
              <div key={d.id} data-testid={`library-doc-${d.id}`} className="bg-white border border-border p-5 flex items-start gap-4">
                <div className="w-10 h-10 bg-secondary flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold leading-snug">{d.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {d.code && <span className="font-mono mr-2">{d.code}</span>}{catName(d.category)}
                  </p>
                </div>
                <button onClick={() => toggleBookmark(d)} data-testid={`library-bookmark-${d.id}`}
                  title={d.bookmarked ? "Remove bookmark" : "Bookmark for quick access"}
                  className={`shrink-0 p-2 border transition-colors ${d.bookmarked ? "border-amber-400 text-amber-500 bg-amber-50" : "border-border text-muted-foreground hover:border-amber-400 hover:text-amber-500"}`}>
                  <Star className="w-4 h-4" fill={d.bookmarked ? "currentColor" : "none"} />
                </button>
                <button onClick={() => download(d)} disabled={downloading === d.id} data-testid={`library-download-${d.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-primary border border-border px-3 py-2 hover:border-primary disabled:opacity-50">
                  {downloading === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Chip({ active, children, ...props }) {
  return (
    <button {...props}
      className={`text-sm px-4 py-2 border transition-colors ${active ? "bg-primary text-white border-primary" : "bg-white border-border hover:border-primary"}`}>
      {children}
    </button>
  );
}
